import { OrderingValues } from '@round/api';
import { ScrollView, TableProps } from '@round/ui-kit';
import styles from './TopAudiosTable.module.css';
import { Table, assets } from '@round/whitelabel-ui';
import { PaginationToolbar, PaginationProps } from '@round/whitelabel-ui';
import { mapOrderingToTableSorting, mapTableSortingToOrdering } from '@round/whitelabel-ui';
import { InstagramTopAudiosTableRow } from '../Instagram/InstagramTopAudios';
import { TiktokTopAudiosTableRow } from '../Tiktok/TiktokTopAudios';
import cn from 'classnames';

const { CrossCircle } = assets;

export type TopAudiosTableRow = InstagramTopAudiosTableRow | TiktokTopAudiosTableRow;

export type Meta = {
    isLoading?: boolean;
};

type Props<TRow extends TopAudiosTableRow, TOrdering extends string> = Pick<
    TableProps<TRow>,
    'data' | 'columns' | 'onRowClick'
> &
    PaginationProps & {
        hasError: boolean;
        ordering?: OrderingValues<TOrdering>;
        onOrderingChange?: (ordering: OrderingValues<TOrdering>[]) => void;
        meta: Meta;
    };

const TopAudiosTable = <TRow extends TopAudiosTableRow, TOrdering extends string>({
    data,
    columns,
    meta,
    hasError,
    page,
    count,
    setPage,
    pageSize,
    setPageSize,
    ordering,
    onOrderingChange,
    onRowClick,
}: Props<TRow, TOrdering>) => {
    const initialLoading = !data.length && meta.isLoading;
    const emptyRows = new Array(6).fill({}) as TRow[];
    const rows = initialLoading ? emptyRows : data;

    return (
        <>
            <ScrollView className={styles.scrollWrapper}>
                <Table
                    manualSorting
                    enableSorting
                    enableSortingRemoval
                    sortDescFirst
                    sorting={ordering ? mapOrderingToTableSorting([ordering]) : []}
                    onSortingChange={
                        onOrderingChange
                            ? (sortingState) => onOrderingChange(mapTableSortingToOrdering(sortingState))
                            : undefined
                    }
                    className={cn(styles.table, { [styles.rowClickable]: !!onRowClick })}
                    data={rows}
                    columns={columns}
                    meta={meta}
                    noDataLabel={
                        <div className={styles.noDataLabel}>
                            {hasError ? (
                                <div className={styles.error}>
                                    <CrossCircle />
                                    <p>Failed to load audios</p>
                                </div>
                            ) : (
                                <p>No audios found</p>
                            )}
                        </div>
                    }
                    onRowClick={onRowClick}
                />
            </ScrollView>
            <PaginationToolbar
                page={page}
                setPage={setPage}
                pageSize={pageSize}
                setPageSize={setPageSize}
                count={count}
                isLoading={meta.isLoading}
            />
        </>
    );
};

export default TopAudiosTable;
